export interface DefaultCardSettings {
  cardFadeInFrames: number;
  cardDurationFullyUpFrames: number;
  cardFadeOutFrames: number;
  cardSlugFrames: number;
}

export const DEFAULT_CARD_SETTINGS: DefaultCardSettings = {
  cardFadeInFrames: 0,
  cardDurationFullyUpFrames: 0,
  cardFadeOutFrames: 0,
  cardSlugFrames: 0,
};

type DefaultCardSettingKey = keyof DefaultCardSettings;

interface FieldConfig {
  snake: string;
  camel: DefaultCardSettingKey;
}

const FIELD_CONFIGS: FieldConfig[] = [
  { snake: 'card_fade_in_frames', camel: 'cardFadeInFrames' },
  { snake: 'card_duration_fully_up_frames', camel: 'cardDurationFullyUpFrames' },
  { snake: 'card_fade_out_frames', camel: 'cardFadeOutFrames' },
  { snake: 'card_slug_frames', camel: 'cardSlugFrames' },
];

function toInt(value: unknown, fallback: number): number {
  const numericValue: number =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : Number.NaN;

  return Number.isFinite(numericValue) ? Math.trunc(numericValue) : fallback;
}

function normalizeDefaultCardSettings(raw: Record<string, unknown>): DefaultCardSettings {
  const normalized: DefaultCardSettings = { ...DEFAULT_CARD_SETTINGS };
  for (const field of FIELD_CONFIGS) {
    const rawValue: unknown = raw[field.snake] ?? raw[field.camel];
    const defaultValue: number = DEFAULT_CARD_SETTINGS[field.camel];
    normalized[field.camel] = toInt(rawValue, defaultValue);
  }
  return normalized;
}

export function parseDefaultCardSettings(value: unknown): DefaultCardSettings {
  if (!value) return DEFAULT_CARD_SETTINGS;

  if (typeof value === 'object') {
    return normalizeDefaultCardSettings(value as Record<string, unknown>);
  }

  if (typeof value !== 'string') return DEFAULT_CARD_SETTINGS;

  try {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === 'object') {
      return normalizeDefaultCardSettings(parsed as Record<string, unknown>);
    }
  } catch {
    return DEFAULT_CARD_SETTINGS;
  }

  return DEFAULT_CARD_SETTINGS;
}

export function serializeDefaultCardSettings(settings: DefaultCardSettings): string {
  return JSON.stringify({
    card_fade_in_frames: settings.cardFadeInFrames,
    card_duration_fully_up_frames: settings.cardDurationFullyUpFrames,
    card_fade_out_frames: settings.cardFadeOutFrames,
    card_slug_frames: settings.cardSlugFrames,
  });
}
